import {
  ButtonHTMLAttributes,
  createContext,
  forwardRef,
  PropsWithChildren,
  useContext,
} from 'react'
import { ComponentProps } from 'preact/compat'
import { NavLink, NavLinkProps } from 'react-router-dom'

import { cx, sva } from 'styled-system/css'
import { HStack } from 'styled-system/jsx'
import { Skeleton } from 'ui-kit/skeleton'
import { Table as UiKitTable } from 'ui-kit/table'
import { Text } from 'ui-kit/text'

const tableRecipe = sva({
  className: 'extendedTable',
  slots: ['row', 'rowClickableElement', 'rowSkeleton'],
  base: {
    row: {
      position: 'relative',
      _hover: {
        '& a:not(.extendedTable__rowClickableElement)': {
          isolation: 'isolate',
        },
        '& button:not(.extendedTable__rowClickableElement)': {
          isolation: 'isolate',
        },
      },
    },
    rowClickableElement: {
      position: 'absolute',
      inset: 0,
      cursor: 'pointer',
    },
    rowSkeleton: {
      '& [class*="skeleton"]:first-child': {
        width: 'full',
        maxWidth: 'xs',
      },
      '&:nth-child(3n+1) [class*="skeleton"]:first-child': {
        maxWidth: 'sm',
      },
      '&:nth-child(5n+1) [class*="skeleton"]:first-child': {
        maxWidth: 'md',
      },
      '&:nth-child(7n+1) [class*="skeleton"]:first-child': {
        maxWidth: 'lg',
      },
    },
  },
})

const tableRecipeClassnames = tableRecipe()

const ClickableElementContext = createContext(false)

type RootProps = UiKitTable.RootProps
type RowProps = ComponentProps<typeof UiKitTable.Row>
type CellProps = ComponentProps<typeof UiKitTable.Cell>
type HeaderProps = ComponentProps<typeof UiKitTable.Header>

function Header({ children, ...props }: HeaderProps) {
  return (
    <UiKitTable.Header {...props}>
      <ClickableElementContext.Provider value={true}>
        {children}
      </ClickableElementContext.Provider>
    </UiKitTable.Header>
  )
}

const Cell = forwardRef<HTMLTableCellElement, CellProps>(
  ({ children, ...props }, ref) => {
    return (
      <UiKitTable.Cell ref={ref} {...props}>
        <ClickableElementContext.Provider value={true}>
          {children}
        </ClickableElementContext.Provider>
      </UiKitTable.Cell>
    )
  },
)

const Row = forwardRef<HTMLTableRowElement, RowProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <UiKitTable.Row
        ref={ref}
        className={cx(tableRecipeClassnames.row, className)}
        {...props}
      >
        {children}
      </UiKitTable.Row>
    )
  },
)

function FullRowButton({
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  const hasParent = useContext(ClickableElementContext)

  if (!hasParent) {
    throw new Error(
      'FullRowButton component must be used inside a Cell or Header component',
    )
  }

  return (
    <button
      type='button'
      className={tableRecipeClassnames.rowClickableElement}
      {...props}
    >
      <Text as={'span'} srOnly>
        {children}
      </Text>
    </button>
  )
}

function FullRowLink({
  children,
  ...props
}: PropsWithChildren<Omit<NavLinkProps, 'children'>>) {
  return (
    <NavLink className={tableRecipeClassnames.rowClickableElement} {...props}>
      {/* Add some margin to keep the element from looking awkward when it's focused on. */}
      <Text as={'span'} srOnly margin={1}>
        {children}
      </Text>
    </NavLink>
  )
}

function RowSkeleton({
  className,
  colSpan,
  ...props
}: Omit<RowProps, 'children'> & { colSpan?: number }) {
  return (
    <Row
      className={cx(tableRecipeClassnames.rowSkeleton, className)}
      {...props}
    >
      <Cell colSpan={colSpan}>
        <HStack>
          <Skeleton height='4' />
          <Skeleton width='4' height='4' />
        </HStack>
      </Cell>
    </Row>
  )
}

const Table = {
  ...UiKitTable,
  Row,
  Header,
  Cell,
  FullRowButton,
  FullRowLink,
  RowSkeleton,
}

export { Table }

export type { RootProps, RowProps, CellProps, HeaderProps }
